<template>
  <div class="min-h-screen bg-gray-100 flex items-center justify-center p-4">
    <div class="w-full max-w-lg bg-white shadow-md rounded-lg p-6">
      <div class="flex flex-col space-y-4">
        <!-- Input and Button Container -->
        <div class="flex items-center space-x-4">
          <input
            v-model="text"
            placeholder="Enter text"
            class="flex-1 p-4 text-lg border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
          />
          <button
            @click="sendData"
            :disabled="isLoading"
            class="relative bg-blue-500 text-white px-6 py-4 rounded-md hover:bg-blue-600 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <span v-if="!isLoading">Test</span>
            <svg
              v-else
              class="animate-spin h-6 w-6 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              ></path>
            </svg>
          </button>
        </div>

        <!-- Response Time Display -->
        <div v-if="responseTime !== null" class="text-center text-green-600">
          First response time: {{ responseTime }} ms
        </div>

        <!-- Error Message Display -->
        <div v-if="errorMessage" class="text-center text-red-600">
          {{ errorMessage }}
        </div>

        <!-- Playing Audio Status -->
        <div v-if="isPlaying" class="text-center text-blue-500">
          Playing audio...
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onBeforeMount, onBeforeUnmount, ref } from "vue";
import { Howl } from "howler";
import { WEB_SOCKET_BASE_URL } from "@/api/api";
import { useWebSocket } from "@/hooks/useWebSocket";

const text = ref("");
const audioQueue = ref([]);
const isPlaying = ref(false);
const isLoading = ref(false);
const responseTime = ref(null);
const errorMessage = ref(null);
let sendTimestamp = null;

const { socket, connect, disconnect } = useWebSocket(
  `${WEB_SOCKET_BASE_URL}/chat`,
  {
    maxRetries: 5,
    reconnectDelay: 2000,
    onMessage: async (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("WebSocket received message: ", data);
        if (data.type !== "audioChunk") return;
        const base64Data = data.chunk; // Receive base64 string
        const audioChunk = base64ToArrayBuffer(base64Data);
        processAudioChunk(audioChunk);
      } catch (error) {
        console.error("Error handling audio chunk:", error);
        errorMessage.value = "Failed to process audio chunk.";
      }
    },
    onOpen: () => {
      console.log("WebSocket connection opened.");
      errorMessage.value = null;
    },
    onClose: () => {
      console.log("WebSocket connection closed.");
    },
    onError: (error) => {
      console.error("WebSocket error:", error);
      errorMessage.value = "WebSocket connection error.";
    },
  }
);

onBeforeMount(() => {
  connect();
});

onBeforeUnmount(() => {
  disconnect();
});

const sendData = () => {
  if (text.value.trim() === "") {
    alert("Please enter a message.");
    return;
  }

  if (!socket.value && socket.value.readyState !== WebSocket.OPEN) {
    connect(); // connect again if not connected
  }

  if (socket.value && socket.value.readyState === WebSocket.OPEN) {
    sendTimestamp = Date.now();
    isLoading.value = true;
    errorMessage.value = null;
    responseTime.value = null; // Reset previous response time
    socket.value.send(
      JSON.stringify({
        type: "text",
        question: text.value,
      })
    );
  } else {
    alert("WebSocket is not connected.");
  }
};

const base64ToArrayBuffer = (base64) => {
  const binaryString = atob(base64); // Decode base64 to binary string
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

const processAudioChunk = (chunk) => {
  const blob = new Blob([chunk], { type: "audio/mp3" }); // Adjust MIME type as needed
  const url = URL.createObjectURL(blob);
  audioQueue.value.push(url); // Add the audio chunk to the queue

  if (sendTimestamp && responseTime.value === null) {
    responseTime.value = Date.now() - sendTimestamp;
    sendTimestamp = null;
    isLoading.value = false;
  }

  if (!isPlaying.value) {
    playNextAudio();
  }
};

const playNextAudio = () => {
  if (audioQueue.value.length === 0) {
    isPlaying.value = false;
    return;
  }

  isPlaying.value = true;
  const url = audioQueue.value.shift(); // Get the next chunk in the queue

  const sound = new Howl({
    src: [url],
    format: ["mp3"], // Specify format if known
    html5: true,
    autoplay: false,
    onend: () => {
      playNextAudio(); // Play the next chunk when this one ends
    },
    onplayerror: (id, err) => {
      console.error("Error playing audio:", err);
      playNextAudio(); // Skip to the next chunk if an error occurs
    },
  });

  sound.play();
};
</script>

<style scoped>
/* Responsive adjustments */
@media (max-width: 640px) {
  input {
    font-size: 1.1rem;
  }
  button {
    padding: 1rem;
  }
}
</style>
